import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../slices/types';
import { setViewMode } from '../../slices/users/reducer';
import { 
  fetchAdminUsers, 
  fetchUserStats,
  createUser, 
  updateUser, 
  singlePointsOperation, 
  bulkPointsOperation, 
  assignApp,
  blockUser,
  changeLoginType,
  type User,
  type CreateUserData,
  type UpdateUserData,
  type PointsOperation,
  type AppAssignment,
  type BlockUserData,
  type ChangeLoginTypeData
} from '../../slices/users/adminUsersSlice';
import { 
  Grid, 
  List, 
  Search, 
  Filter, 
  MoreHorizontal, 
  UserPlus, 
  Edit, 
  Coins, 
  Smartphone, 
  Activity,
  RefreshCw,
  Calendar,
  Users as UsersIcon,
  UserCheck,
  TrendingUp,
  BarChart3,
  Ban,
  Unlock,
  Key,
} from 'lucide-react';
import { sendMessageToUser, type SendMessagePayload } from '../../slices/users/adminUsersSlice';
import SendNotification from './SendNotification';

const Notifications: React.FC = () => {
    const dispatch = useAppDispatch();
    const { users: adminUsers, loading: adminLoading, error: adminError, stats, selectedUsers, currentOperation, totalCount, filteredCount } = useAppSelector((state) => state.AdminUsers);
    const { viewMode } = useAppSelector((state) => state.Users);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [showStats, setShowStats] = useState(true);
    const [selectedPeriod, setSelectedPeriod] = useState('Last 30 days');
    const [hasInitialized, setHasInitialized] = useState(false);
    
    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showPointsModal, setShowPointsModal] = useState(false);
    const [showBulkPointsModal, setShowBulkPointsModal] = useState(false);
    const [showAppAssignmentModal, setShowAppAssignmentModal] = useState(false);
    const [showBlockModal, setShowBlockModal] = useState(false);
    const [showLoginTypeModal, setShowLoginTypeModal] = useState(false);
    const [showMessageModal, setShowMessageModal] = useState(false);
    const composerRef = useRef<HTMLDivElement | null>(null);
    const navigate = useNavigate();
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [messageForm, setMessageForm] = useState<SendMessagePayload>({ user_id: 0, message: '', title: '' });

  // Notification UI-only state (no integration yet)
  const [notifyScope, setNotifyScope] = useState<'broadcast' | 'users' | 'user'>('user');
  const [notificationType, setNotificationType] = useState<'system' | 'offer'>('system');
  const [selectedAppId, setSelectedAppId] = useState<string>('');
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [singleUserId, setSingleUserId] = useState<number | null>(null);
  const [dataJson, setDataJson] = useState<string>('');

  // Dummy apps list for broadcast scope (replace with real data during integration)
  const dummyApps: { id: string; name: string }[] = [
    { id: '90d8de53-2ac6-4e91-b521-010da104aca9', name: 'Main App' },
    { id: '11111111-1111-1111-1111-111111111111', name: 'Lite App' },
    { id: '22222222-2222-2222-2222-222222222222', name: 'Beta App' },
  ];
    
    // Form states
    const [createForm, setCreateForm] = useState<CreateUserData>({
        mobile_number: '',
        password: '',
        first_name: '',
        last_name: '',
        app_id: '90d8de53-2ac6-4e91-b521-010da104aca9',
        initial_points: 0,
        is_staff: false,
        is_active: true,
    });
    
    const [updateForm, setUpdateForm] = useState<UpdateUserData>({
        user_id: 0,
        first_name: '',
        last_name: '',
        email: '',
        points: 0,
        is_active: true,
        is_staff: false,
    });
    
    const [blockForm, setBlockForm] = useState<BlockUserData>({
        operation: 'block' as 'block' | 'unblock',
        user_id: 0,
    });
    
    const [loginTypeForm, setLoginTypeForm] = useState<ChangeLoginTypeData>({
        operation: 'change_login_type',
        user_id: 0,
        new_login_type: 'mpin',
        new_credential: '',
    });
    
    const [pointsForm, setPointsForm] = useState<PointsOperation>({
        user_id: 0,
        action: 'add',
        amount: 0,
    });
    
    const [bulkPointsForm, setBulkPointsForm] = useState({
        action: 'add' as 'add' | 'deduct',
        amount: 0,
        user_ids: [] as number[],
        mobile_numbers: [] as string[],
    });
    
    const [appAssignmentForm, setAppAssignmentForm] = useState<AppAssignment>({
        app_id: '90d8de53-2ac6-4e91-b521-010da104aca9',
        user_ids: [],
        remove_app: false,
    });

    // Debug: Log the entire Redux state
    const fullState = useAppSelector((state) => state);
    console.log('Full Redux state:', fullState);
    console.log('AdminUsers slice state:', fullState.AdminUsers);

    useEffect(() => {
        if (!hasInitialized) {
            console.log('Users component mounted, dispatching fetchAdminUsers');
            console.log('Current Redux state before dispatch:', { adminUsers, adminLoading, adminError });
            dispatch(fetchAdminUsers({}));
        dispatch(fetchUserStats(selectedPeriod));
            setHasInitialized(true);
        }
    }, [dispatch, selectedPeriod, hasInitialized]);

    useEffect(() => {
        // Initialize filteredUsers with all users when data loads
        if (adminUsers.length > 0) {
            console.log('Users data loaded, setting filteredUsers:', adminUsers.length);
            setFilteredUsers(adminUsers);
        }
    }, [adminUsers]);

    useEffect(() => {
        // Filter users based on search term with debounce
        if (adminUsers.length > 0) {
            const timeoutId = setTimeout(() => {
                const filtered = adminUsers.filter((user: User) =>
                    (user.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.mobile_number.includes(searchTerm) ||
                    (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (user.is_staff ? 'Staff' : 'User').toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredUsers(filtered);
            }, 300); // 300ms debounce

            return () => clearTimeout(timeoutId);
        }
    }, [adminUsers, searchTerm]);

    const handleViewModeChange = (mode: 'list' | 'grid') => {
        console.log('Changing view mode to:', mode);
        dispatch(setViewMode(mode));
    };

    // Handle period change
    const handlePeriodChange = (period: string) => {
        setSelectedPeriod(period);
        dispatch(fetchUserStats(period));
    };

    // Handle user actions
    const handleEditUser = (user: User) => {
        setSelectedUser(user);
        console.log('Editing user:', user);
        console.log('User points (raw):', user.points, 'Type:', typeof user.points);
        
        const pointsValue = typeof user.points === 'string' ? parseFloat(user.points) : (user.points || 0);
        console.log('Converted points:', pointsValue);
        
        setUpdateForm({
            user_id: user.id,
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email || '',
            points: pointsValue,
            is_active: user.is_active,
            is_staff: user.is_staff || false,
        });
        setShowUpdateModal(true);
    };

    const handlePointsOperation = (user: User) => {
        setSelectedUser(user);
        setPointsForm({
            user_id: user.id,
            action: 'add',
            amount: 0,
        });
        setShowPointsModal(true);
    };

    const handleAppAssignment = (user: User) => {
        setSelectedUser(user);
        setAppAssignmentForm(prev => ({
            ...prev,
            user_ids: [user.id],
        }));
        setShowAppAssignmentModal(true);
    };

    const handleBlockUser = (user: User) => {
        setSelectedUser(user);
        const isBlocked = !user.is_active || user.status_display === 'Inactive';
        setBlockForm({
            operation: (isBlocked ? 'unblock' : 'block') as 'block' | 'unblock',
            user_id: user.id,
        });
        setShowBlockModal(true);
    };

    const handleChangeLoginType = (user: User) => {
        setSelectedUser(user);
        setLoginTypeForm({
            operation: 'change_login_type',
            user_id: user.id,
            new_login_type: user.login_type === 'mpin' ? 'password' : 'mpin',
            new_credential: '',
        });
        setShowLoginTypeModal(true);
    };

    const handleOpenMessage = (user: User) => {
        navigate('/notifications/send', { state: { defaultUser: user } });
    };

    const handleSendMessage = async () => {
        try {
            await dispatch(sendMessageToUser(messageForm)).unwrap();
            setShowMessageModal(false);
        } catch (error) {
            console.error('Error sending message:', error);
        }
    };

    // Form submission handlers
    const handleCreateUser = async () => {
        try {
            await dispatch(createUser(createForm)).unwrap();
            setShowCreateModal(false);
            setCreateForm({
                mobile_number: '',
                password: '',
                first_name: '',
                last_name: '',
                app_id: '90d8de53-2ac6-4e91-b521-010da104aca9',
                initial_points: 0,
                is_staff: false,
                is_active: true,
            });
            dispatch(fetchAdminUsers({}));
        } catch (error) {
            console.error('Error creating user:', error);
        }
    };

    const handleUpdateUser = async () => {
        try {
            await dispatch(updateUser(updateForm)).unwrap();
            setShowUpdateModal(false);
            dispatch(fetchAdminUsers({}));
        } catch (error) {
            console.error('Error updating user:', error);
        }
    };

    const handlePointsSubmit = async () => {
        try {
            await dispatch(singlePointsOperation(pointsForm)).unwrap();
            setShowPointsModal(false);
            dispatch(fetchAdminUsers({}));
        } catch (error) {
            console.error('Error updating points:', error);
        }
    };

    const handleBulkPointsSubmit = async () => {
        try {
            await dispatch(bulkPointsOperation(bulkPointsForm)).unwrap();
            setShowBulkPointsModal(false);
            dispatch(fetchAdminUsers({}));
        } catch (error) {
            console.error('Error updating bulk points:', error);
        }
    };

    const handleAppAssignmentSubmit = async () => {
        try {
            await dispatch(assignApp(appAssignmentForm)).unwrap();
            setShowAppAssignmentModal(false);
            dispatch(fetchAdminUsers({}));
        } catch (error) {
            console.error('Error assigning app:', error);
        }
    };

    const handleBlockSubmit = async () => {
        try {
            await dispatch(blockUser(blockForm)).unwrap();
            setShowBlockModal(false);
            dispatch(fetchAdminUsers({}));
        } catch (error) {
            console.error('Error updating user status:', error);
        }
    };

    const handleLoginTypeSubmit = async () => {
        try {
            await dispatch(changeLoginType(loginTypeForm)).unwrap();
            setShowLoginTypeModal(false);
            dispatch(fetchAdminUsers({}));
        } catch (error) {
            console.error('Error changing login type:', error);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Debug logging
    console.log('Users state:', { adminUsers: adminUsers.length, adminLoading, adminError, viewMode, totalCount, filteredUsers: filteredUsers.length });

    if (adminLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading users...</p>
                </div>
            </div>
        );
    }

    if (adminError) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="text-red-500 text-xl mb-4">Error: {adminError}</div>
                    <button 
                        onClick={() => dispatch(fetchAdminUsers({}))}
                        className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // Show message if no users
    if (adminUsers.length === 0 && !adminLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="text-gray-500 text-xl mb-4">No users found</div>
                    <button 
                        onClick={() => dispatch(fetchAdminUsers({}))}
                        className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90"
                    >
                        Refresh
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Notifications</h1>
                    <p className="text-gray-600 dark:text-gray-400">Review users and send notifications</p>
                </div>
                <div className="flex gap-2">
                    {/** Stats toggle button removed for Notifications */}
                    {false && (
                        <button
                            onClick={() => setShowStats(!showStats)}
                            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                showStats 
                                    ? 'text-white bg-blue-600 hover:bg-blue-700' 
                                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            <Activity className="w-4 h-4" />
                            {showStats ? 'Hide Stats' : 'Show Stats'}
                        </button>
                    )}
                    {/** Create user button removed for Notifications */}
                    {false && (
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <UserPlus className="w-4 h-4" />
                            Create User
                        </button>
                    )}
                    <button
                        onClick={() => {
                            dispatch(fetchAdminUsers({}));
                            dispatch(fetchUserStats(selectedPeriod));
                        }}
                        disabled={adminLoading}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${adminLoading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Stats Dashboard - commented out intentionally for Notifications page */}
            {/*
            {showStats && stats && (
                <div className="mb-6">...statistics content...</div>
            )}
            */}

            {/* Fallback Stats (when stats not available) - commented out */}
            {false && !showStats && (<div />)}

            {/* Controls */}
            <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Search users..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                            <Filter className="w-5 h-5" />
                        </button>
                        <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                            <button
                                onClick={() => handleViewModeChange('list')}
                                className={`p-2 ${viewMode === 'list' ? 'bg-primary text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                            >
                                <List className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => handleViewModeChange('grid')}
                                className={`p-2 ${viewMode === 'grid' ? 'bg-primary text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                            >
                                <Grid className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Debug Info */}
            {/* <div className="mb-4 p-3 bg-gray-100 rounded text-sm">
                <p>View Mode: <strong>{viewMode}</strong> | Users: <strong>{users.length}</strong> | Filtered: <strong>{filteredUsers.length}</strong></p>
                <p>Total Count: <strong>{totalCount}</strong> | Loading: <strong>{loading ? 'Yes' : 'No'}</strong> | Error: <strong>{error || 'None'}</strong></p>
                <button 
                    onClick={() => dispatch(testAction())}
                    className="mt-2 bg-blue-500 text-white px-3 py-1 rounded text-xs hover:bg-blue-600"
                >
                    Test Redux Store
                </button>
            </div> */}

            {/* Content */}
            {viewMode === 'list' ? (
                <ListView 
                    users={filteredUsers} 
                    formatDate={formatDate}
                    onEditUser={handleEditUser}
                    onPointsOperation={handlePointsOperation}
                    onAppAssignment={handleAppAssignment}
                    onBlockUser={handleBlockUser}
                    onChangeLoginType={handleChangeLoginType}
                    onMessageUser={handleOpenMessage}
                />
            ) : (
                <GridView 
                    users={filteredUsers} 
                    formatDate={formatDate}
                    onEditUser={handleEditUser}
                    onPointsOperation={handlePointsOperation}
                    onAppAssignment={handleAppAssignment}
                    onBlockUser={handleBlockUser}
                    onChangeLoginType={handleChangeLoginType}
                    onMessageUser={handleOpenMessage}
                />
            )}

            {/* Inline composer removed in favor of dedicated page */}

            {/* Modals */}
            {/* Create User Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">Create New User</h3>
                        {/* Scope Selection */}
                        

                        {/* Broadcast: Apps list */}
                        {notifyScope === 'broadcast' && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Select App</label>
                            <div className="space-y-2 max-h-40 overflow-auto border rounded-md p-3">
                              {dummyApps.map(app => (
                                <label key={app.id} className="flex items-center gap-2 text-sm">
                                  <input type="radio" name="app" checked={selectedAppId===app.id} onChange={()=>setSelectedAppId(app.id)} />
                                  <span className="font-medium">{app.name}</span>
                                  <span className="ml-auto text-xs text-gray-500">{app.id}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Users: Multi-select table */}
                        {notifyScope === 'users' && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Select Users</label>
                            <div className="border rounded-md max-h-56 overflow-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-3 py-2 w-10"><input type="checkbox" onChange={(e)=>{
                                      if(e.target.checked){ setSelectedUserIds(adminUsers.map(u=>u.id)); }
                                      else { setSelectedUserIds([]); }
                                    }} checked={selectedUserIds.length>0 && selectedUserIds.length===adminUsers.length} /></th>
                                    <th className="px-3 py-2 text-left">User</th>
                                    <th className="px-3 py-2 text-left">Mobile</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {adminUsers.map(u => (
                                    <tr key={u.id} className="odd:bg-white even:bg-gray-50">
                                      <td className="px-3 py-2"><input type="checkbox" checked={selectedUserIds.includes(u.id)} onChange={(e)=>{
                                        setSelectedUserIds(prev=> e.target.checked ? [...prev, u.id] : prev.filter(id=>id!==u.id));
                                      }} /></td>
                                      <td className="px-3 py-2">{u.full_name}</td>
                                      <td className="px-3 py-2">{u.mobile_number}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <div className="mt-2 text-xs text-gray-600">{selectedUserIds.length} users selected</div>
                          </div>
                        )}

                        {/* User: Single user selector */}
                        {notifyScope === 'user' && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">User</label>
                            <select value={singleUserId ?? ''} onChange={(e)=> setSingleUserId(Number(e.target.value)||null)} className="w-full px-3 py-2 border rounded-md">
                              <option value="">Select user</option>
                              {adminUsers.map(u=> (
                                <option key={u.id} value={u.id}>{u.full_name} ({u.mobile_number})</option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Type */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium mb-1">Type</label>
                          <select value={notificationType} onChange={(e)=> setNotificationType(e.target.value as 'system'|'offer')} className="w-full px-3 py-2 border rounded-md">
                            <option value="system">System</option>
                            <option value="offer">Offer</option>
                          </select>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Title (optional)</label>
                                <input
                                    type="text"
                                    value={messageForm.title || ''}
                                    onChange={(e) => setMessageForm(prev => ({ ...prev, title: e.target.value }))}
                                    className="w-full px-3 py-2 border rounded-md"
                                    placeholder="Enter title"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Message</label>
                                <textarea
                                    value={messageForm.message}
                                    onChange={(e) => setMessageForm(prev => ({ ...prev, message: e.target.value }))}
                                    className="w-full px-3 py-2 border rounded-md h-32 resize-y"
                                    placeholder="Write your message..."
                                />
                            </div>
                            {notifyScope === 'users' && (
                              <div>
                                <label className="block text-sm font-medium mb-1">Data (optional JSON)</label>
                                <input
                                  type="text"
                                  value={dataJson}
                                  onChange={(e)=> setDataJson(e.target.value)}
                                  className="w-full px-3 py-2 border rounded-md"
                                  placeholder='e.g. {"promo_code":"EXTRA2"}'
                                />
                              </div>
                            )}
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={() => {
                                  // UI-only: build payload preview and close
                                  let payload: any = { scope: notifyScope, title: messageForm.title, message: messageForm.message, type: notificationType };
                                  if (notifyScope === 'broadcast') payload.app_id = selectedAppId || undefined;
                                  if (notifyScope === 'users') {
                                    payload.user_ids = selectedUserIds;
                                    try { payload.data = dataJson ? JSON.parse(dataJson) : undefined; } catch {}
                                  }
                                  if (notifyScope === 'user') payload.user_id = singleUserId || undefined;
                                  console.log('Notification payload (preview):', payload);
                                  setShowMessageModal(false);
                                }}
                                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
                                disabled={!messageForm.message.trim() ||
                                  (notifyScope==='broadcast' && !selectedAppId) ||
                                  (notifyScope==='users' && selectedUserIds.length===0) ||
                                  (notifyScope==='user' && !singleUserId)
                                }
                            >
                                Send
                            </button>
                            <button
                                onClick={() => setShowMessageModal(false)}
                                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Create User Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">Create New User</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Mobile Number</label>
                                <input
                                    type="text"
                                    value={createForm.mobile_number}
                                    onChange={(e) => setCreateForm(prev => ({ ...prev, mobile_number: e.target.value }))}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Password</label>
                                <input
                                    type="password"
                                    value={createForm.password}
                                    onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">First Name</label>
                                    <input
                                        type="text"
                                        value={createForm.first_name}
                                        onChange={(e) => setCreateForm(prev => ({ ...prev, first_name: e.target.value }))}
                                        className="w-full px-3 py-2 border rounded-md"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Last Name</label>
                                    <input
                                        type="text"
                                        value={createForm.last_name}
                                        onChange={(e) => setCreateForm(prev => ({ ...prev, last_name: e.target.value }))}
                                        className="w-full px-3 py-2 border rounded-md"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Initial Points</label>
                                <input
                                    type="number"
                                    value={createForm.initial_points}
                                    onChange={(e) => setCreateForm(prev => ({ ...prev, initial_points: Number(e.target.value) }))}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </div>
                            <div className="flex items-center gap-4">
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={createForm.is_staff}
                                        onChange={(e) => setCreateForm(prev => ({ ...prev, is_staff: e.target.checked }))}
                                        className="mr-2"
                                    />
                                    Staff User
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={createForm.is_active}
                                        onChange={(e) => setCreateForm(prev => ({ ...prev, is_active: e.target.checked }))}
                                        className="mr-2"
                                    />
                                    Active
                                </label>
                            </div>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={handleCreateUser}
                                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
                            >
                                Create User
                            </button>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Update User Modal */}
            {showUpdateModal && selectedUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">Update User</h3>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">First Name</label>
                                    <input
                                        type="text"
                                        value={updateForm.first_name}
                                        onChange={(e) => setUpdateForm(prev => ({ ...prev, first_name: e.target.value }))}
                                        className="w-full px-3 py-2 border rounded-md"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Last Name</label>
                                    <input
                                        type="text"
                                        value={updateForm.last_name}
                                        onChange={(e) => setUpdateForm(prev => ({ ...prev, last_name: e.target.value }))}
                                        className="w-full px-3 py-2 border rounded-md"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Email</label>
                                <input
                                    type="email"
                                    value={updateForm.email}
                                    onChange={(e) => setUpdateForm(prev => ({ ...prev, email: e.target.value }))}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Points</label>
                                <input
                                    type="number"
                                    value={updateForm.points || 0}
                                    onChange={(e) => setUpdateForm(prev => ({ ...prev, points: Number(e.target.value) }))}
                                    className="w-full px-3 py-2 border rounded-md"
                                    placeholder="Enter points"
                                />
                                {/* Debug: {updateForm.points} */}
                            </div>
                            <div className="flex items-center gap-4">
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={updateForm.is_staff}
                                        onChange={(e) => setUpdateForm(prev => ({ ...prev, is_staff: e.target.checked }))}
                                        className="mr-2"
                                    />
                                    Staff User
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={updateForm.is_active}
                                        onChange={(e) => setUpdateForm(prev => ({ ...prev, is_active: e.target.checked }))}
                                        className="mr-2"
                                    />
                                    Active
                                </label>
                            </div>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={handleUpdateUser}
                                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
                            >
                                Update User
                            </button>
                            <button
                                onClick={() => setShowUpdateModal(false)}
                                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Points Operation Modal */}
            {showPointsModal && selectedUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">Manage Points - {selectedUser.full_name}</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Action</label>
                                <select
                                    value={pointsForm.action}
                                    onChange={(e) => setPointsForm(prev => ({ ...prev, action: e.target.value as 'add' | 'deduct' }))}
                                    className="w-full px-3 py-2 border rounded-md"
                                >
                                    <option value="add">Add Points</option>
                                    <option value="deduct">Deduct Points</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Amount</label>
                                <input
                                    type="number"
                                    value={pointsForm.amount || ''}
                                    onChange={(e) => setPointsForm(prev => ({ ...prev, amount: Number(e.target.value) || 0 }))}
                                    placeholder="Enter amount"
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </div>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={handlePointsSubmit}
                                className="flex-1 bg-yellow-600 text-white py-2 px-4 rounded-md hover:bg-yellow-700"
                            >
                                Update Points
                            </button>
                            <button
                                onClick={() => setShowPointsModal(false)}
                                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* App Assignment Modal */}
            {showAppAssignmentModal && selectedUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">Assign App - {selectedUser.full_name}</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">App ID</label>
                                <input
                                    type="text"
                                    value={appAssignmentForm.app_id}
                                    onChange={(e) => setAppAssignmentForm(prev => ({ ...prev, app_id: e.target.value }))}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={appAssignmentForm.remove_app}
                                    onChange={(e) => setAppAssignmentForm(prev => ({ ...prev, remove_app: e.target.checked }))}
                                    className="mr-2"
                                />
                                <label className="text-sm font-medium">Remove App Assignment</label>
                            </div>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={handleAppAssignmentSubmit}
                                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700"
                            >
                                {appAssignmentForm.remove_app ? 'Remove App' : 'Assign App'}
                            </button>
                            <button
                                onClick={() => setShowAppAssignmentModal(false)}
                                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Block/Unblock User Modal */}
            {showBlockModal && selectedUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md transform transition-all">
                        {/* Header */}
                        {(() => {
                            const isBlocked = !selectedUser.is_active || selectedUser.status_display === 'Inactive';
                            return (
                                <div className={`px-6 py-4 rounded-t-xl ${
                                    isBlocked 
                                        ? 'bg-green-50 border-b border-green-200' 
                                        : 'bg-red-50 border-b border-red-200'
                                }`}>
                                    <div className="flex items-center">
                                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                                            isBlocked 
                                                ? 'bg-green-100' 
                                                : 'bg-red-100'
                                        }`}>
                                            {isBlocked ? (
                                                <Unlock className="w-5 h-5 text-green-600" />
                                            ) : (
                                                <Ban className="w-5 h-5 text-red-600" />
                                            )}
                                        </div>
                                        <div className="ml-4">
                                            <h3 className={`text-lg font-semibold ${
                                                isBlocked ? 'text-green-800' : 'text-red-800'
                                            }`}>
                                                {isBlocked ? 'Unblock User' : 'Block User'}
                                            </h3>
                                            <p className="text-sm text-gray-600">
                                                {selectedUser.full_name} ({selectedUser.mobile_number})
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Content */}
                        <div className="px-6 py-4">
                            {(() => {
                                const isBlocked = !selectedUser.is_active || selectedUser.status_display === 'Inactive';
                                return (
                                    <>
                                        <div className={`p-4 rounded-lg border ${
                                            isBlocked 
                                                ? 'bg-green-50 border-green-200' 
                                                : 'bg-red-50 border-red-200'
                                        }`}>
                                            <div className="flex">
                                                <div className="flex-shrink-0">
                                                    {isBlocked ? (
                                                        <Unlock className="w-5 h-5 text-green-400" />
                                                    ) : (
                                                        <Ban className="w-5 h-5 text-red-400" />
                                                    )}
                                                </div>
                                                <div className="ml-3">
                                                    <h4 className={`text-sm font-medium ${
                                                        isBlocked ? 'text-green-800' : 'text-red-800'
                                                    }`}>
                                                        {isBlocked ? 'Unblock Confirmation' : 'Block Confirmation'}
                                                    </h4>
                                                    <p className={`mt-1 text-sm ${
                                                        isBlocked ? 'text-green-700' : 'text-red-700'
                                                    }`}>
                                                        {isBlocked 
                                                            ? 'This user will regain access to the system and be able to log in again.' 
                                                            : 'This user will lose access to the system and will not be able to log in.'
                                                        }
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* User Details */}
                                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                            <h5 className="text-sm font-medium text-gray-700 mb-2">User Details:</h5>
                                            <div className="space-y-1 text-sm text-gray-600">
                                                <div><span className="font-medium">Name:</span> {selectedUser.full_name}</div>
                                                <div><span className="font-medium">Mobile:</span> {selectedUser.mobile_number}</div>
                                                <div><span className="font-medium">Status:</span> 
                                                    <span className={`ml-1 px-2 py-1 text-xs rounded-full ${
                                                        isBlocked 
                                                            ? 'bg-red-100 text-red-800' 
                                                            : 'bg-green-100 text-green-800'
                                                    }`}>
                                                        {isBlocked ? 'Currently Blocked' : 'Currently Active'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>

                        {/* Actions */}
                        <div className="px-6 py-4 bg-gray-50 rounded-b-xl">
                            {(() => {
                                const isBlocked = !selectedUser.is_active || selectedUser.status_display === 'Inactive';
                                return (
                                    <div className="flex gap-3">
                                        <button
                                            onClick={handleBlockSubmit}
                                            className={`flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                                                isBlocked 
                                                    ? 'bg-green-600 hover:bg-green-700 focus:ring-2 focus:ring-green-500' 
                                                    : 'bg-red-600 hover:bg-red-700 focus:ring-2 focus:ring-red-500'
                                            }`}
                                        >
                                            {isBlocked ? (
                                                <Unlock className="w-4 h-4 mr-2" />
                                            ) : (
                                                <Ban className="w-4 h-4 mr-2" />
                                            )}
                                            {isBlocked ? 'Unblock User' : 'Block User'}
                                        </button>
                                        <button
                                            onClick={() => setShowBlockModal(false)}
                                            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* Change Login Type Modal */}
            {showLoginTypeModal && selectedUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">Change Login Type - {selectedUser.full_name}</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Current Login Type</label>
                                <div className="px-3 py-2 bg-gray-100 rounded-md text-sm">
                                    {selectedUser.login_type || 'Unknown'}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">New Login Type</label>
                                <select
                                    value={loginTypeForm.new_login_type}
                                    onChange={(e) => setLoginTypeForm(prev => ({ 
                                        ...prev, 
                                        new_login_type: e.target.value as 'mpin' | 'password' 
                                    }))}
                                    className="w-full px-3 py-2 border rounded-md"
                                >
                                    <option value="mpin">MPIN</option>
                                    <option value="password">Password</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    New {loginTypeForm.new_login_type === 'mpin' ? 'MPIN' : 'Password'}
                                </label>
                                <input
                                    type={loginTypeForm.new_login_type === 'mpin' ? 'text' : 'password'}
                                    value={loginTypeForm.new_credential}
                                    onChange={(e) => setLoginTypeForm(prev => ({ ...prev, new_credential: e.target.value }))}
                                    placeholder={`Enter new ${loginTypeForm.new_login_type === 'mpin' ? 'MPIN' : 'password'}`}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </div>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={handleLoginTypeSubmit}
                                className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700"
                            >
                                Change Login Type
                            </button>
                            <button
                                onClick={() => setShowLoginTypeModal(false)}
                                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// List View Component
const ListView: React.FC<{ 
    users: User[]; 
    formatDate: (date: string) => string;
    onEditUser: (user: User) => void;
    onPointsOperation: (user: User) => void;
    onAppAssignment: (user: User) => void;
    onBlockUser: (user: User) => void;
    onChangeLoginType: (user: User) => void;
    onMessageUser: (user: User) => void;
}> = ({ users, formatDate, onEditUser, onPointsOperation, onAppAssignment, onBlockUser, onChangeLoginType, onMessageUser }) => (
    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
            <table className="w-full">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Login Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0 h-10 w-10">
                                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                            <span className="text-primary font-semibold text-sm">
                                                {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="ml-4">
                                        <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                                        <div className="text-sm text-gray-500">ID: {user.id}</div>
                                    </div>
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{user.mobile_number}</div>
                                {user.email && <div className="text-sm text-gray-500">{user.email}</div>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center space-x-2">
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-blue-800">
                                        {user.is_staff ? 'Staff' : 'User'}
                                </span>
                                    {(!user.is_active || user.status_display === 'Inactive') && (
                                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                            <Ban className="w-3 h-3 mr-1" />
                                            Blocked
                                        </span>
                                    )}
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {user.points_formatted}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    user.is_active 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-red-100 text-red-800'
                                }`}>
                                    {user.status_display}
                                </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    user.login_type === 'mpin' 
                                        ? 'bg-orange-100 text-blue-800' 
                                        : user.login_type === 'password'
                                        ? 'bg-purple-100 text-purple-800'
                                        : 'bg-gray-100 text-gray-800'
                                }`}>
                                    {user.login_type || 'Unknown'}
                                </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(user.created_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center gap-2">
                                    <button 
                                        onClick={() => onMessageUser(user)}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-orange-50 rounded-md hover:bg-orange-100 transition-colors"
                                        title="Message User"
                                    >
                                        Message
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

// Grid View Component
const GridView: React.FC<{ 
    users: User[]; 
    formatDate: (date: string) => string;
    onEditUser: (user: User) => void;
    onPointsOperation: (user: User) => void;
    onAppAssignment: (user: User) => void;
    onBlockUser: (user: User) => void;
    onChangeLoginType: (user: User) => void;
    onMessageUser: (user: User) => void;
}> = ({ users, formatDate, onEditUser, onPointsOperation, onAppAssignment, onBlockUser, onChangeLoginType, onMessageUser }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {users.map((user) => (
            <div key={user.id} className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                            <span className="text-primary font-semibold text-lg">
                                {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                            </span>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-gray-900">{user.full_name}</h3>
                            <p className="text-xs text-gray-500">ID: {user.id}</p>
                        </div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-600">
                        <MoreHorizontal className="w-4 h-5" />
                    </button>
                </div>
                
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Mobile:</span>
                        <span className="text-sm text-gray-900">{user.mobile_number}</span>
                    </div>
                    
                    {user.email && (
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Email:</span>
                            <span className="text-sm text-gray-900 truncate">{user.email}</span>
                        </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Role:</span>
                        <div className="flex items-center space-x-1">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-blue-800">
                                {user.is_staff ? 'Staff' : 'User'}
                        </span>
                            {(!user.is_active || user.status_display === 'Inactive') && (
                                <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                    <Ban className="w-3 h-3 mr-1" />
                                    Blocked
                                </span>
                            )}
                        </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Points:</span>
                        <span className="text-sm font-medium text-gray-900">{user.points_formatted}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Status:</span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                        }`}>
                            {user.status_display}
                        </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Login Type:</span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.login_type === 'mpin' 
                                ? 'bg-orange-100 text-blue-800' 
                                : user.login_type === 'password'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-gray-100 text-gray-800'
                        }`}>
                            {user.login_type || 'Unknown'}
                        </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Joined:</span>
                        <span className="text-xs text-gray-500">{formatDate(user.created_at)}</span>
                    </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-1 gap-2">
                        <button 
                            onClick={() => onMessageUser(user)}
                            className="inline-flex items-center justify-center bg-blue-600 text-white py-2 px-3 rounded-lg text-xs hover:bg-blue-700 transition-colors"
                        >
                            Message
                        </button>
                    </div>
                </div>
            </div>
        ))}
    </div>
);

export default Notifications;










